import axios from 'axios';
import React, { useEffect, useReducer, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { logout } from './apicalls/logout';
import LoadingComponent from './components/LoadingComponent';
import { MenuTreeChild, MenuList } from './components/topicPath/const/MenuTree';
import { accessibleMenuTreeId } from './GlobalState';

// 初期ステータス
const initialState = {
  loading: true,
  error: false,
  result: undefined
};

// Http通信の状態管理
const reducer = (state: any, action: any) => {
  switch (action.type) {
    // 取得中
    case 'FETCH':
      return { ...state, loading: true };
    // 取得成功
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, err: false, result: action.result };
    // 取得失敗
    case 'FETCH_FAILED':
      return { ...state, loading: false, err: true, result: true };
  }
};

/**
 * 指定されたパスから最初のセグメントを取得する関数。
 *
 * 例えば、"/menu/overview" のようなパスが与えられた場合は、"/menu" を返す。
 * パスが "/" だけの場合は "/" を返す。
 */
const getFirstPathSegment = (path: string) => {
  const segments = path.split('/').filter(segment => segment !== '');
  return segments.length > 0 ? `/${segments[0]}` : '/';
};

/**
 * 指定されたパスから、アクセスしようとしているメニューを取得する関数。
 */
const getAccessingMenu = (path: string): MenuTreeChild | undefined => {
  const firstPathSegment = getFirstPathSegment(path);
  return MenuList.find(menu => menu.urls.includes(firstPathSegment));
};

type Tprops = {
  children?: React.ReactNode;
};

export const AccessibleMenu: React.FC<Tprops> = props => {
  const loginPageUrl = '/';

  // 表示するコンポーネント
  const { children } = props;
  // http通信のステータスを管理
  const [state, dispatch] = useReducer(reducer, initialState);
  // 遷移先のページurl
  const location = useLocation();
  // アクセス可能メニューツリーのID
  const [, setAccessibleeMenuTree] = useRecoilState<any>(accessibleMenuTreeId);
  // 画面遷移のルーター
  const history = useHistory();
  // リダイレクト先 デフォルト「工事」画面
  const [redirectPath, setRedirectPath] = useState<string>('/constructions');

  useEffect(
    () => {
      // アクセスしようとしているメニューを取得
      const accessingMenu = getAccessingMenu(location.pathname);
      if (accessingMenu !== undefined) {
        // メニューがあればアクセス可能メニューの取得を行う
        dispatch({ type: 'FETCH' });
      } else {
        // メニューがない場合は何もしない
        dispatch({ type: 'FETCH_SUCCESS', result: true });
        return;
      }

      // アクセス可能メニューを取得
      axios
        .get('/api/get/users/accessible-menu', {
          headers: {
            withCredentials: true
          }
        })
        .then(function (response) {
          setRedirectPath('/constructions');
          // アクセス可能なメニューidのリスト
          const accessibleList: string[] = response.data.accessible_menu_tree;
          setAccessibleeMenuTree(accessibleList);

          // アクセス可能メニューリストに遷移先のメニューがあればtrue
          const isExist = accessibleList.includes(accessingMenu.menuId);
          if (!isExist) {
            window.alert('アクセス権限がありません');
          }
          dispatch({ type: 'FETCH_SUCCESS', result: isExist });
        })
        .catch(err => {
          setRedirectPath('/');
          if (location.pathname != loginPageUrl) {
            window.alert(
              'システムエラーが発生しました。再度ログインしてください。'
            );
            // エラー発生時はアラートを表示してログアウト
            logout().then(() => {
              history.push('/');
            });
          }
          dispatch({ type: 'FETCH_FAILED' });
        });
    },
    [
      // location.pathname,
      // このコンポーネントは全てのページの親であり、location.pathnameを依存配列に入れると
      // ページ遷移するたびに再レンダリングがされてしまうため、location.pathnameを削除
      // 後からテナントのプランを変更した場合、一旦リロードしないとメニューが変わらない。
      // ログイン時間に制限をつけたり、バックエンド側でテナントのプランによるエンドポイントの制限
      // をつける対応を行う必要がある。
    ]
  );

  // アクセス権限がない場合はリダイレクト
  return (
    <>
      {state.loading ? (
        <LoadingComponent />
      ) : state.result ? (
        children
      ) : (
        <Redirect to={{ pathname: redirectPath }} />
      )}
    </>
  );
};
